import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Grid,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import { adminApiClient } from "../services/apiClient";

interface SystemSetting {
  key: string;
  value: string; // JSON string for user_deleted: keys
  description?: string;
}

interface AdminUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
}

interface DeletePreviewResponse {
  user: AdminUser;
  preview: {
    counts: Record<string, number>;
    ids?: Record<string, unknown>;
    summaryOnly?: boolean;
    limitApplied?: number;
    userRow?: { willDelete: boolean };
  };
}

type ExpectedCounts = Record<string, number>;

const parseMarker = (
  setting: SystemSetting,
): { userId: string; requestedAt?: string } | null => {
  if (!setting.key.startsWith("user_deleted:")) return null;
  const userId = setting.key.replace("user_deleted:", "");
  try {
    const data = JSON.parse(setting.value) as { requestedAt?: string };
    return { userId, requestedAt: data.requestedAt };
  } catch {
    return { userId };
  }
};

const DeletionRequestsPage: React.FC = () => {
  const [settings, setSettings] = useState<SystemSetting[]>([]);
  const [loadingSettings, setLoadingSettings] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [summaryOnly, setSummaryOnly] = useState<boolean>(true);
  const [limit, setLimit] = useState<number>(200);
  const [preview, setPreview] = useState<
    DeletePreviewResponse["preview"] | null
  >(null);
  const [loadingPreview, setLoadingPreview] = useState<boolean>(false);
  const [confirming, setConfirming] = useState<boolean>(false);
  const [restoring, setRestoring] = useState<boolean>(false);

  // Load system settings and filter deletion markers
  const loadSettings = async (): Promise<void> => {
    setLoadingSettings(true);
    setError(null);
    try {
      const res = await adminApiClient.get("/admin/settings");
      setSettings(res.data.settings as SystemSetting[]);
    } catch {
      setError("Failed to load settings");
    } finally {
      setLoadingSettings(false);
    }
  };

  useEffect(() => {
    void loadSettings();
  }, []);

  const deletionRequests = useMemo(() => {
    return settings
      .filter((s) => s.key.startsWith("user_deleted:"))
      .map((s) => parseMarker(s))
      .filter((x): x is { userId: string; requestedAt?: string } => !!x);
  }, [settings]);

  const loadUser = async (userId: string): Promise<void> => {
    setSelectedUser(null);
    setError(null);
    try {
      const res = await adminApiClient.get(`/admin/users/${userId}`);
      setSelectedUser(res.data.user as AdminUser);
      setSelectedUserId(userId);
    } catch {
      setError("User not found or access denied");
    }
  };

  const handleSelectRequest = async (userId: string): Promise<void> => {
    await loadUser(userId);
    setPreview(null);
  };

  const handlePreview = async (): Promise<void> => {
    if (!selectedUserId) return;
    setLoadingPreview(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (summaryOnly) params.set("summaryOnly", "1");
      if (!summaryOnly && limit > 0) params.set("limit", String(limit));
      const res = await adminApiClient.get<DeletePreviewResponse>(
        `/admin/users/${selectedUserId}/delete-preview${params.toString() ? `?${params.toString()}` : ""}`,
      );
      setPreview(res.data.preview);
    } catch {
      setError("Failed to get delete preview");
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleConfirm = async (): Promise<void> => {
    if (!selectedUserId || !preview) return;
    setConfirming(true);
    setError(null);
    try {
      const expectedCounts: ExpectedCounts = preview.counts;
      await adminApiClient.post(
        `/admin/users/${selectedUserId}/delete-confirm`,
        { expectedCounts },
      );
      // Refresh settings list (marker should be cleared)
      await loadSettings();
      setPreview(null);
      setSelectedUser(null);
      setSelectedUserId("");
    } catch {
      setError("Confirm failed (check admin flag or counts changed)");
    } finally {
      setConfirming(false);
    }
  };

  const handleRestore = async (userId?: string): Promise<void> => {
    const id = userId || selectedUserId;
    if (!id) return;
    setRestoring(true);
    setError(null);
    try {
      await adminApiClient.post(`/admin/users/${id}/restore`);
      await loadSettings();
      if (id === selectedUserId) {
        setPreview(null);
      }
    } catch {
      setError("Restore failed");
    } finally {
      setRestoring(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Data Deletion Requests
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Card>
            <CardContent>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Typography variant="h6">Active Requests</Typography>
                <Button
                  onClick={() => void loadSettings()}
                  disabled={loadingSettings}
                >
                  {loadingSettings ? <CircularProgress size={18} /> : "Refresh"}
                </Button>
              </Stack>

              <Paper
                variant="outlined"
                sx={{ maxHeight: 360, overflow: "auto" }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>User ID</TableCell>
                      <TableCell>Requested At</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deletionRequests.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Typography variant="body2" color="text.secondary">
                            No active deletion requests
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {deletionRequests.map((r) => (
                      <TableRow
                        key={r.userId}
                        selected={selectedUserId === r.userId}
                      >
                        <TableCell>{r.userId}</TableCell>
                        <TableCell>
                          {r.requestedAt
                            ? new Date(r.requestedAt).toLocaleString()
                            : "-"}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => void handleSelectRequest(r.userId)}
                            >
                              Select
                            </Button>
                            <Button
                              size="small"
                              variant="text"
                              color="secondary"
                              onClick={() => void handleRestore(r.userId)}
                              disabled={restoring}
                            >
                              Restore
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 7 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Preview & Confirm
              </Typography>
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems="center"
                >
                  <TextField
                    label="User ID"
                    size="small"
                    fullWidth
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => void loadUser(selectedUserId)}
                    disabled={!selectedUserId}
                  >
                    Load
                  </Button>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={summaryOnly}
                        onChange={(_, v) => setSummaryOnly(v)}
                      />
                    }
                    label="Summary only"
                  />
                  <TextField
                    label="Limit"
                    type="number"
                    size="small"
                    value={limit}
                    onChange={(e) =>
                      setLimit(Math.max(1, Number(e.target.value) || 200))
                    }
                    disabled={summaryOnly}
                    sx={{ width: 120 }}
                  />
                </Stack>

                {selectedUser && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedUser.email} ({selectedUser.firstName}{" "}
                    {selectedUser.lastName})
                  </Typography>
                )}

                <Stack direction="row" spacing={2}>
                  <Button
                    variant="contained"
                    onClick={() => void handlePreview()}
                    disabled={!selectedUserId || loadingPreview}
                  >
                    {loadingPreview ? (
                      <CircularProgress size={18} />
                    ) : (
                      "Preview"
                    )}
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => void handleRestore()}
                    disabled={!selectedUserId || restoring}
                  >
                    {restoring ? <CircularProgress size={18} /> : "Restore"}
                  </Button>
                  <Box flexGrow={1} />
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => void handleConfirm()}
                    disabled={!preview || confirming}
                  >
                    {confirming ? (
                      <CircularProgress size={18} />
                    ) : (
                      "Confirm Delete"
                    )}
                  </Button>
                </Stack>

                {preview && (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Counts
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Entity</TableCell>
                          <TableCell align="right">Count</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(preview.counts).map(([k, v]) => (
                          <TableRow key={k}>
                            <TableCell>{k}</TableCell>
                            <TableCell align="right">{v}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {preview.limitApplied && (
                      <Typography variant="caption" color="text.secondary">
                        limit applied: {preview.limitApplied}
                      </Typography>
                    )}
                  </Paper>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DeletionRequestsPage;
